import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/max';
import { E164_PATTERN } from './phone-shape.js';

/**
 * The one telephone normaliser — docs/decisions/0086. Every column, contract,
 * screen and sender that holds a telephone reads it through this module, on
 * libphonenumber-js's **full (`max`) metadata**, about 39 KiB gzipped. The
 * `min` set is half that and validates only a number's length: it takes
 * `0170 12` as a German number, because German numbers vary in length, and
 * refusing a mistyped number is the whole point. The screen's phone field
 * (`react-phone-number-input/input-max`) loads the same set, so a bundle pays
 * for it once, and it is what tells a fixed line from a mobile.
 */

/** Where a national number is read when a person has not chosen a country. */
export const DEFAULT_PHONE_COUNTRY = 'TR';

export { E164_PATTERN, isE164Shaped } from './phone-shape.js';

/**
 * What a telephone may be written with. Anything else — a letter, an
 * extension, a comma between two numbers — is refused rather than read around,
 * because 0024's matching is exact-or-nothing: a value the reader had to
 * interpret is not the value the person typed.
 */
const PHONE_CHARACTERS = /^[\d\s+()./-]+$/;

export type ParsedPhone = {
  /** `+905321112233` — what is stored and what every provider is given. */
  e164: string;
  /** The ISO country the number belongs to, or null for a non-geographic one. */
  country: string | null;
  /** `+90 532 111 22 33` — what a screen shows. */
  international: string;
};

function supportedCountry(value: string | null | undefined): CountryCode | undefined {
  if (!value) return undefined;
  const upper = value.trim().toUpperCase();
  return isSupportedCountry(upper) ? upper : undefined;
}

/**
 * A complete, valid telephone number, or null. A value in international form
 * (`+90 …`, or `0090 …`) is read as it says; a national one (`0532 …`) only
 * against `defaultCountry`, and **never** against a country nobody named — a
 * national number with no country is null, not a guess. A fragment, a number
 * the country's plan does not allow, or any character outside digits and the
 * usual separators is null too.
 */
export function parsePhone(
  value: string | null | undefined,
  defaultCountry?: string | null,
): ParsedPhone | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  if (trimmed === '' || !PHONE_CHARACTERS.test(trimmed)) return null;

  const country = supportedCountry(defaultCountry);
  const compact = trimmed.replace(/[^\d+]/g, '');
  const text = compact.startsWith('00') ? `+${compact.slice(2)}` : compact;

  const parsed = country
    ? parsePhoneNumberFromString(text, { defaultCountry: country, extract: false })
    : parsePhoneNumberFromString(text, { extract: false });
  if (!parsed?.isValid()) return null;
  if (!E164_PATTERN.test(parsed.number)) return null;

  return {
    e164: parsed.number,
    country: parsed.country ?? null,
    international: parsed.formatInternational(),
  };
}

/** `parsePhone`'s E.164, or null. */
export function normalizePhone(
  value: string | null | undefined,
  defaultCountry?: string | null,
): string | null {
  return parsePhone(value, defaultCountry)?.e164 ?? null;
}

/** Whether a value is already a valid E.164 number — what a contract accepts. */
export function isValidE164(value: string): boolean {
  return E164_PATTERN.test(value) && parsePhone(value)?.e164 === value;
}

/**
 * A stored telephone as a person reads it: `+90 532 111 22 33` for a number
 * that parses, and the value exactly as it was typed for one that does not —
 * an unconverted legacy number is shown as it is, never hidden or reshaped.
 */
export function formatPhone(value: string | null | undefined): string {
  if (value === null || value === undefined) return '';
  return parsePhone(value)?.international ?? value;
}

/** The country an E.164 number belongs to — what a screen preselects on edit. */
export function phoneCountryOf(value: string | null | undefined): string | null {
  return parsePhone(value)?.country ?? null;
}

export type PhoneCountry = { code: string; callingCode: string };

/** Every country the metadata knows, with its calling code, for the selector. */
export function phoneCountries(): PhoneCountry[] {
  return getCountries().map((code) => ({ code, callingCode: getCountryCallingCode(code) }));
}

/** `+90` for `TR`, or null for a code the metadata does not know. */
export function phoneCallingCodeOf(country: string | null | undefined): string | null {
  const supported = supportedCountry(country);
  return supported ? getCountryCallingCode(supported) : null;
}

/**
 * Whether a number is a fixed line — `true` only for one the metadata types as
 * `FIXED_LINE`. A number it cannot tell apart (`FIXED_LINE_OR_MOBILE`, most of
 * North America), a mobile, or a value that does not parse is `false`, because
 * what it answers is a warning a person may ignore, never a refusal.
 */
export function isFixedLinePhone(value: string | null | undefined): boolean {
  if (!value) return false;
  const parsed = parsePhoneNumberFromString(value.trim(), { extract: false });
  return parsed?.isValid() === true && parsed.getType() === 'FIXED_LINE';
}
